import { dbItem } from "../../Shared/sharedInterfaces";
import * as functions from "./sharedFunctions";
import specialItemConfig from "../config/exceptions/specialItemConfig.json";
import moduleAliasConfig from "../config/exceptions/moduleAliasConfig.json";

const getSpecialConfigKey = (moduleType: string): keyof typeof specialItemConfig | undefined => {
  const normalized = moduleType.toLowerCase();

  if (normalized === "dalim" || normalized === "qdm" || normalized === "qdm01") {
    return "qdm";
  }

  if (normalized === "dmx" || normalized === "ser485/dmx") {
    return "dmx";
  }

  return normalized as keyof typeof specialItemConfig;
};

export interface InnerMapValue2 {
  used: number;
  vrij: number;
  modType: string;
  modId: number;
  usedadresses: number;
  ctd: number;
}

interface modToAdd {
  count: number;
  iMapKey: string;
  valueKey: string;
}

const moduleFromType = (moduleType: string): string => {
  const relTypes = ["ROL", "hand", "fan", "stopc"];

  return relTypes.includes(moduleType) ? "REL" : moduleType;
};

const normalizeStoredModuleType = (moduleType: string): string => {
  const normalized = String(moduleType || "").toUpperCase();
  const realModuleName =
    moduleAliasConfig.storedAliasToRealName[
      normalized as keyof typeof moduleAliasConfig.storedAliasToRealName
    ];
  if (realModuleName) {
    return realModuleName;
  }
  return moduleType;
};

/*
Deze functie bouwt een map van modules gebaseerd op het type uitgang, het aantal gebruikte uitgangen en de instellingen onlySa en rel04_08.
- moduleType1: Het type module (bijv. "REL", "DIM").
- aantal: Het aantal uitgangen op deze module
- onlySa: Een boolean die aangeeft of alleen SA-modules moeten worden gebruikt.
- rel04_08: Een boolean die aangeeft of REL04SA-modules moeten worden gebruikt.
- io_mod: Een boolean die aangeeft of IO-modules moeten worden gebruikt.
- ctdValue: Een numerieke waarde die wordt gebruikt voor de berekening van CTD.

vb REL kan REL04SA of REL08 zijn, DIM kan DIM02SA, DIM04SA zijn, afhankelijk van instellingen.
De functie bepaalt eerst het juiste moduleType op basis van de input en instellingen, haalt vervolgens de moduleparameters op en berekent hoeveel modules er nodig zijn om aan het aantal uitgangen te voldoen.
Het resultaat is een map van modules met hun gebruikte hoeveelheid, vrije capaciteit, type, ID, gebruikte adressen en CTD-waarde.
*/
function buildRealModulesMap(
  moduleType1: string,
  aantal: number,
  onlySa: boolean,
  rel04_08: boolean,
  io_mod: boolean,
  ctdValue: number,
  specialItems?: dbItem[],
) {
  // DIM - DIM02SA

  let moduleType2 = moduleType1;

  let moduleType = moduleFromType(moduleType2);
  let module = functions.getModule(moduleType, onlySa);

  if (!io_mod) {
    module = functions.getModuleByName(moduleType1);

    // Voor module-uitgangen: ook de nextModule-keten ophalen zodat de
    // verdeling de kleinere opvolgmodule kan gebruiken voor resterende uitgangen.
    const nextChain: typeof module.retModules = [];
    const dbMods: import("../../Shared/sharedInterfaces").ModuleTypes[] = JSON.parse(
      sessionStorage.getItem("DbModules") || "[]",
    );
    let current = module.retModules[0];
    while (current?.nextModule) {
      const next = dbMods.find((m) => m.id === current.nextModule);
      if (!next) break;
      nextChain.push(next);
      current = next;
    }
    if (nextChain.length > 0) {
      module = {
        retModules: [...module.retModules, ...nextChain].sort(
          (a, b) => b.outputs - a.outputs,
        ),
        retCounter: module.retCounter,
      };
    }
  }

  const moduleParameters = module.retModules;
  function createNestedMap(): Map<string, InnerMapValue2> {
    return new Map();
  }

  let modulesMap = createNestedMap();

  let outp = {};
  let modulesO: Array<{
    outputs: number;
    type: string;
    counter: number;
    vrij: number;
    modType: string;
    modId: number;
    adresses: number;
    usedadresses: number;
  }> = [];

  // We need to loop through the module parameters and calculate how many modules we need based on the aantal and outputs of each module
  // type REL -> 2 parameters (REL04SA en REL08)

  moduleParameters.forEach((mod) => {
    if (moduleType === "REL") {
      if (rel04_08 === false) {
        let modi = {
          outputs: mod.outputs,
          type: mod.name,
          counter: 0,
          vrij: 0,
          modType: moduleType,
          modId: mod.id,
          adresses: mod.adresses,
          usedadresses: 0,
        };
        modulesO.push(modi);
      } else {
        if (mod.name === "REL04SA") {
          let modi = {
            outputs: mod.outputs,
            type: mod.name,
            counter: 0,
            vrij: 0,
            modType: moduleType,
            modId: mod.id,
            adresses: mod.adresses,
            usedadresses: 0,
          };
          modulesO.push(modi);
        }
      }
    } else {
      let modi = {
        outputs: mod.outputs,
        type: mod.name,
        counter: 0,
        vrij: 0,
        modType: moduleType,
        modId: mod.id,
        adresses: mod.adresses,
        usedadresses: 0,
      };
      modulesO.push(modi);
    }
  });

  let modc = Object.keys(modulesO).length;
  let newo = aantal;

  /*
  De logica hier is als volgt:
  - We itereren door de beschikbare modules (modulesO) 
  en berekenen hoeveel modules van elk type nodig zijn om aan het aantal uitgangen (aantal) te voldoen.
  - Voor elke module berekenen we hoeveel volledige modules we nodig hebben (teller1) 
  en hoeveel uitgangen er overblijven (oversc). 
  - Afhankelijk van de waarde van oversc en de positie in de modulelijst (i) passen we de counter en vrij aan voor elke module.
  - We zorgen ervoor dat we altijd eerst de modules met de meeste uitgangen vullen, 
  zodat we het aantal benodigde modules minimaliseren.
  - Uiteindelijk vullen we de modulesMap met de berekende waarden voor elke module die nodig is.
  */

  // Voor DMX (en toekomstige types) houden we rekening met:
  // 1. outputMultiplier per dmxType: het effectief aantal uitgangsadressen per item
  // 2. channelsPerItem per dmxType: het aantal kanalen dat een item verbruikt (max 48 per module)

  let effectiveOutputs = 0;
  let totalChannels = 0;
  let totalNormalOutputs = 0;
  let totalGroupOutputs = 0;

  if (specialItems && specialItems.length > 0) {
    const busTypeKey = getSpecialConfigKey(moduleType);
    if (!busTypeKey) {
      // No special config for this module type.
    }
    const busConfig = busTypeKey ? specialItemConfig[busTypeKey] : undefined;

    if (busConfig) {
      specialItems.forEach((item) => {
        const typeField = (busConfig as any).typeField;
        const defaultType = (busConfig as any).defaultType;
        const rawType =
          (item.outputOptions as any)[typeField] ??
          (item.outputOptions as any).dmxType ??
          defaultType;
        const typeKey = String(rawType) as keyof typeof busConfig.types;
        const typeConfig = busConfig.types[typeKey] as any;

        if (typeConfig) {
          if ((busConfig as any).mode === "weightedChannels") {
            effectiveOutputs += typeConfig.outputMultiplier ?? 1;
            totalChannels += typeConfig.channelsPerItem ?? 1;
          }

          if ((busConfig as any).mode === "dualLimit") {
            // For group items, qdmFixtureCount holds how many individual addresses the group spans.
            const fixtureCount = (item.outputOptions as any).qdmFixtureCount ?? 0;
            totalNormalOutputs += (typeConfig.outputUnits ?? 1) + fixtureCount;
            totalGroupOutputs += typeConfig.groupUnits ?? 0;
          }
        }
      });
    }
  }

  // Voor specialItems (bv. DMX) bepalen enkel de kanalen het aantal modules, niet de adressen.
  // Adressen worden wel bijgehouden in usedadresses.
  const isSpecialBus =
    specialItems !== undefined &&
    specialItems.length > 0 &&
    totalChannels > 0;

  const moduleBusTypeKey = getSpecialConfigKey(moduleType);
  const moduleBusConfig = moduleBusTypeKey
    ? (specialItemConfig[moduleBusTypeKey] as any)
    : undefined;

  const isDualLimitSpecial =
    specialItems !== undefined &&
    specialItems.length > 0 &&
    moduleBusConfig?.mode === "dualLimit";

  if (isDualLimitSpecial) {
    const busConfig = moduleBusConfig;

    if (busConfig && modulesO.length > 0) {
      const maxOutputsPerModule = busConfig.maxOutputsPerModule ?? modulesO[0].outputs;
      const maxGroupsPerModule = busConfig.maxGroupsPerModule ?? 0;
      const requiredModulesByOutputs = Math.ceil(totalNormalOutputs / maxOutputsPerModule);
      const requiredModulesByGroups =
        maxGroupsPerModule > 0
          ? Math.ceil(totalGroupOutputs / maxGroupsPerModule)
          : 0;
      const requiredModules = Math.max(requiredModulesByOutputs, requiredModulesByGroups);

      const primaryMod = modulesO[0];
      primaryMod.counter = requiredModules;
      primaryMod.vrij = Math.max(
        0,
        requiredModules * maxOutputsPerModule - totalNormalOutputs,
      );
      primaryMod.usedadresses = totalNormalOutputs * primaryMod.adresses;
    }
  } else if (isSpecialBus) {
    const busConfig = moduleBusConfig;

    if (busConfig && modulesO.length > 0) {
      const requiredModules = Math.ceil(totalChannels / busConfig.maxChannelsPerModule);
      const primaryMod = modulesO[0];

      primaryMod.counter = requiredModules;
      // Vrij: voor special bus types (zoals DMX) tellen we vrije kanalen i.p.v. vrije uitgangen
      primaryMod.vrij = Math.max(
        0,
        requiredModules * busConfig.maxChannelsPerModule - totalChannels,
      );
      // Adressenbehoud: bijhouden hoeveel adressen effectief gebruikt worden
      primaryMod.usedadresses = effectiveOutputs * primaryMod.adresses;
    }
  } else {
    for (let i = 0; i < modc; i++) {
      let currOutputs = modulesO[i].outputs;
      let nextOutputs = 0;
      // First mod
      if (i < modc - 1) {
        nextOutputs = modulesO[i + 1].outputs;
      }
      let teller1 = Math.floor(newo / currOutputs);
      let oversc = newo - teller1 * currOutputs;

      if (i === 0 && teller1 >= 1 && oversc !== 0) {
        modulesO[i].counter = teller1;
        modulesO[i].vrij = 0;
        modulesO[i].usedadresses =
          modulesO[i].counter * modulesO[i].outputs * modulesO[i].adresses -
          modulesO[i].vrij * modulesO[i].adresses;
      }

      if (oversc < currOutputs && oversc > nextOutputs) {
        modulesO[i].counter++;
        modulesO[i].vrij = currOutputs - oversc;
        modulesO[i].usedadresses =
          modulesO[i].counter * modulesO[i].outputs * modulesO[i].adresses -
          modulesO[i].vrij * modulesO[i].adresses;
        break;
      } else if (oversc === currOutputs) {
        modulesO[i].counter++;
        modulesO[i].vrij = currOutputs - oversc;
        modulesO[i].usedadresses =
          modulesO[i].counter * modulesO[i].outputs * modulesO[i].adresses -
          modulesO[i].vrij * modulesO[i].adresses;
      }

      if (i === 0 && oversc === 0) {
        modulesO[i].counter = Math.floor(aantal / currOutputs);
        modulesO[i].vrij = 0;
        modulesO[i].usedadresses =
          modulesO[i].counter * modulesO[i].outputs * modulesO[i].adresses -
          modulesO[i].vrij * modulesO[i].adresses;
        break;
      } else if (oversc === 0) {
        modulesO[i].counter++;
        modulesO[i].vrij = 0;
        modulesO[i].usedadresses =
          modulesO[i].counter * modulesO[i].outputs * modulesO[i].adresses -
          modulesO[i].vrij * modulesO[i].adresses;
        break;
      }
    }
  }

  /*
  After calculating the required modules and their free capacity, 
  we populate the modulesMap with the final values for each module type that is needed.
  We only add modules to the map if their counter is greater than 0, 
  meaning they are required to meet the number of outputs needed.
  Each entry in the modulesMap includes the number of used modules, 
  the free capacity, the module type, module ID, used addresses, and the CTD value.
  */
  modulesO.forEach((mod) => {
    if (mod.counter > 0) {
      let outp: InnerMapValue2 = {
        used: mod.counter,
        vrij: mod.vrij,
        modType: mod.modType,
        modId: mod.modId,
        usedadresses: mod.usedadresses,
        ctd: ctdValue,
      };
      modulesMap.set(mod.type, outp);
    }
  });
  return modulesMap;
}

/*
function buildRealModulesMap2(moduleType: string, aantal: number) {
  // Here is aantal - aantal adressen - should be aantal modules
  debugger
  function createNestedMap(): Map<string, InnerMapValue2> {
    return new Map();
  }

  let modulesMap = createNestedMap();

  function getModulePage(
    data: dbItem[],
    page: number,
    moduletype: string,
    modCounter: number,
  ) {
    // page is 1-based (1 = first 4 items, 2 = next 4, ...)
    const filtered = data.filter(
      (item) => item.outputOptions.moduletype === moduletype,
    );

    const start = (page - 1) * modCounter;
    const end = start + modCounter;

    return filtered.slice(start, end);
  }

  let counter = 1;
  let mods = [];
  for (let i = 0; i < aantal; i++) {
    let modToAdd = {} as modToAdd;
    const mod = functions.getModuleFromName(moduleType ?? "");

    modToAdd.valueKey = moduleType;
    if (mod) {
      //const outputs = getModulePage(items, counter, moduleType, mod.outputs);
      modToAdd.iMapKey = mod.type;
      const maxOutp = mod.outputs;
      modToAdd.count = Math.ceil(counter / maxOutp); // Round up otherwise we could have 0.75 modules
      counter = counter + 1;
    }
    mods.push(modToAdd);
  }
  
  const nestedMap: Map<string, Map<string, InnerMapValue2>> = new Map();
  //const nestedMap = iMap;

  function createInnerValue(modType: string, count: number): InnerMapValue2 {
    return {
      used: count,
      vrij: 0,
      modType,
      modId: Date.now(),
      usedadresses: count,
      ctd: 0
    };
  }

  for (const mod of mods) {
    const { iMapKey, valueKey, count } = mod;
    if (!iMapKey || !valueKey) continue;

    let innerMap = nestedMap.get(iMapKey);
    if (!innerMap) {
      innerMap = new Map<string, InnerMapValue2>();
      nestedMap.set(iMapKey, innerMap);
    }

    const existing = innerMap.get(valueKey);
    if (existing) {
      existing.used += count;
    } else {
      modulesMap.set(valueKey, createInnerValue(iMapKey, count));
    }
  }
debugger
  return modulesMap;
}
*/
function sortDbItems(arr: dbItem[]): dbItem[] {
  return arr.sort((a, b) => {
    // Compare type (alphabetically)
    if (a.type < b.type) return -1;
    if (a.type > b.type) return 1;

    // Compare ctdId numerically (treat undefined as -1)
    const ctdA = a.outputOptions.ctdId ?? -1;
    const ctdB = b.outputOptions.ctdId ?? -1;
    if (ctdA !== ctdB) return ctdA - ctdB;

    // Compare kastId numerically (treat undefined as -1)
    const kastA = a.outputOptions.kastId ?? -1;
    const kastB = b.outputOptions.kastId ?? -1;

    return kastA - kastB;
  });
}

const buildModulesMap = (items: dbItem[]) => {
  let buildedModulesMap = new Map<string, number>();

  items.forEach((item) => {
    let currentCounter: number = 0;
    let newCounter: number = 0;
    //item.icon.key = database value module type (REL, DIM)

    if (item.type) {
      let treeiconkey = item.type;

      if (
        treeiconkey === "ROLO" ||
        treeiconkey === "hand" ||
        treeiconkey === "fan" ||
        treeiconkey === "stopc"
      ) {
        treeiconkey = "REL";
      }

      // item.icon.key = REL, DIM
      currentCounter = buildedModulesMap.get(treeiconkey)!;
      newCounter = currentCounter + 1;

      if (newCounter) {
        buildedModulesMap.set(treeiconkey, newCounter);
      } else {
        buildedModulesMap.set(treeiconkey, 1);
      }
    }
  });
  
  return buildedModulesMap;
};

const buildModulesMapForModules = (items: dbItem[]) => {
  let buildedModulesMap = new Map<string, number>();
  // just count the outputs here for each type
  items.forEach((item) => {
    let currentCounter: number = 0;
    let newCounter: number = 0;
    if (item.outputOptions.moduletype) {
      const normalizedModuleType = normalizeStoredModuleType(
        item.outputOptions.moduletype,
      );
      currentCounter = buildedModulesMap.get(normalizedModuleType)!;
      newCounter = currentCounter + 1;
      if (newCounter) {
        buildedModulesMap.set(normalizedModuleType, newCounter);
      } else {
        buildedModulesMap.set(normalizedModuleType, 1);
      }
    }
  });
  return buildedModulesMap;
};

function mergeMaps<V>(
  map1: Map<string, Map<string, V>>,
  map2: Map<string, Map<string, V>>,
): Map<string, Map<string, V>> {
  const result = new Map(map1);

  for (const [outerKey, innerMap2] of map2) {
    const innerMap1 = result.get(outerKey);

    if (!innerMap1) {
      result.set(outerKey, new Map(innerMap2));
    } else {
      for (const [innerKey, value] of innerMap2) {
        innerMap1.set(innerKey, value); // overwrite duplicates
      }
    }
  }

  return result;
}

function combineMaps(
  map1: Map<string, number>,
  map2: Map<string, number>,
): Map<string, number> {
  const result = new Map<string, number>(map1);

  for (const [key, value] of map2) {
    if (result.has(key)) {
      result.set(key, result.get(key)! + value);
    } else {
      result.set(key, value);
    }
  }

  return result;
}

function addMaps(
  map1: Map<string, Map<string, number>>,
  map2: Map<string, Map<string, number>>,
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();

  // Copy map1 first
  for (const [outerKey, innerMap] of map1) {
    result.set(outerKey, new Map(innerMap));
  }

  for (const [outerKey, innerMap2] of map2) {
    const innerMap1 = result.get(outerKey);

    if (!innerMap1) {
      result.set(outerKey, new Map(innerMap2));
    } else {
      for (const [innerKey, value2] of innerMap2) {
        const value1 = innerMap1.get(innerKey) ?? 0;
        innerMap1.set(innerKey, value1 + value2);
      }
    }
  }

  return result;
}

function createLevel3Map(): Map<string, Map<string, Map<string, number>>> {
  return new Map();
}

function createLevel2Map(): Map<string, Map<string, number>> {
  return new Map();
}

export function getModulesFromSavedDb_2(
  db1: dbItem[],
  onlySa: boolean,
  rel04_08: boolean,
) {
  const db: dbItem[] = sortDbItems(db1);

  // Alle losse uitgangen, zonder kasten
  const dbItemsNoMod: dbItem[] = db.filter(
    (item) =>
      item.outputOptions.moduletype === "" &&
      item.type !== "kast" &&
      item.type !== "controller",
  );

  // Alle uitgangen aangemaakt met module, zonder kasten
  const dbItemsMod: dbItem[] = db.filter(
    (item) =>
      item.outputOptions.moduletype !== "" &&
      item.type !== "kast" &&
      item.type !== "controller",
  );

  const dmxItems: dbItem[] = db.filter(
    (item) => item.type === "DMX" || item.type === "SER485/DMX",
  );

  const qdmItems: dbItem[] = db.filter(
    (item) => item.type === "QDM" || item.type === "QDM01" || item.type === "DALIM",
  );

  const easeewaveItems: dbItem[] = db.filter(
    (item) => item.type.includes("COMWE"),
  );

  const qwizbItems: dbItem[] = db.filter(
    (item) => item.type.includes("COMWZ"),
  );

  // Alle kasten
  const kasten: dbItem[] = db.filter((item) => item.type == "kast");
  const kastIds = kasten.map((item) => item.outputOptions.kastId);

  // Alle controllers
  const controllers: dbItem[] = db.filter((item) => item.type == "controller");
  const controllersIds = controllers.map((item) => item.outputOptions.ctdId);

  // Maak map aan voor meerdere kasten
  /*
  Kastenmap:
    key: kastx
    value: 
        key: ctdx
        value:
            key: iox
            value: nr
  CtdMap:
    key: ctdx
    value: 
        key: kastx
        value:
            key: iox
            value: nr

  */

  // Build kasten
  const kastenMap = createLevel3Map();

  kastIds.forEach((kastId) => {
    const kastKeyIO = "kastIO-" + String(kastId);
    const kastKeyMOD = "kastMOD-" + String(kastId);
    const ctdMapIO = createLevel2Map();
    const ctdMapMod = createLevel2Map();

    controllersIds.forEach((ctdId) => {
      const ctdKeyIO = "ctdIO-" + String(ctdId);
      const ctdKeyMod = "ctdMOD-" + String(ctdId);

      // Get IO's for this kast / ctd
      const outputs: dbItem[] = dbItemsNoMod.filter(
        (item) =>
          item.outputOptions.kastId === kastId &&
          item.outputOptions.ctdId === ctdId,
      );

      // Get Modules for this kast / ctd
      const module_outputs: dbItem[] = dbItemsMod.filter(
        (item) =>
          item.outputOptions.kastId === kastId &&
          item.outputOptions.ctdId === ctdId,
      );

      let outputsMap = new Map<string, number>();
      outputsMap = buildModulesMap(outputs);

      let module_outputsMap = new Map<string, number>();
      module_outputsMap = buildModulesMapForModules(module_outputs);

      let modulesMap = new Map<string, number>();
      modulesMap = combineMaps(outputsMap, module_outputsMap);
      ctdMapIO.set(ctdKeyIO, outputsMap);
      ctdMapMod.set(ctdKeyMod, module_outputsMap);
    });

    kastenMap.set(kastKeyIO, ctdMapIO);
    kastenMap.set(kastKeyMOD, ctdMapMod);
  });

  //Build controllers
  const ctdMap = createLevel3Map();

  controllersIds.forEach((ctdId) => {
    const ctdKey = "ctd-" + String(ctdId);
    const kastenMap = createLevel2Map();

    kastIds.forEach((kastId) => {
      const kastKey = "kast-" + String(kastId);

      // Get IO's for this kast / ctd
      const outputs: dbItem[] = dbItemsNoMod.filter(
        (item) =>
          item.outputOptions.kastId === kastId &&
          item.outputOptions.ctdId === ctdId,
      );

      let modulesMap = new Map<string, number>();
      modulesMap = buildModulesMap(outputs);

      kastenMap.set(kastKey, modulesMap);
    });

    ctdMap.set(ctdKey, kastenMap);
  });

  function createKastenMap(): Map<
    string,
    Map<string, Map<string, InnerMapValue2>>
  > {
    return new Map();
  }

  function createCtdsMap(): Map<string, Map<string, InnerMapValue2>> {
    return new Map();
  }

  function createModMap(): Map<string, InnerMapValue2> {
    return new Map();
  }

  function mergeInnerValue(target: InnerMapValue2, source: InnerMapValue2) {
    target.used += source.used;
    target.vrij += source.vrij;
    target.usedadresses += source.usedadresses;
  }

  function mergeModuleMaps(
    target: Map<string, InnerMapValue2>,
    source: Map<string, InnerMapValue2>,
  ) {
    for (const [key, value] of source) {
      if (target.has(key)) {
        mergeInnerValue(target.get(key)!, value);
      } else {
        target.set(key, { ...value });
      }
    }
  }

  function mergeCtdMaps(
    target: Map<string, Map<string, InnerMapValue2>>,
    ctdKey: string,
    modulesMap: Map<string, InnerMapValue2>,
  ) {
    if (!target.has(ctdKey)) {
      target.set(ctdKey, new Map());
    }

    mergeModuleMaps(target.get(ctdKey)!, modulesMap);
  }

  const module_kastenMap = createKastenMap();

  kastenMap.forEach((kastKey, kastValue) => {
    if (!module_kastenMap.has(kastValue)) {
      module_kastenMap.set(kastValue, createCtdsMap());
    }

    const module_ctds = module_kastenMap.get(kastValue)!;

    kastKey.forEach((ctdKey, ctdValue) => {
      if (!ctdValue.startsWith("ctdIO") && !ctdValue.startsWith("ctdMOD")) {
        return;
      }

      const thisControllerId = Number(ctdValue.split("-").pop());
      let module_modulesMap = createModMap();

      
      const easeewaveForThisControllerIO = easeewaveItems.filter(
        (item) =>
          item.outputOptions.ctdId === thisControllerId &&
          item.outputOptions.moduletype === "",
      );

      const easeewaveForThisControllerMod = easeewaveItems.filter(
        (item) =>
          item.outputOptions.ctdId === thisControllerId &&
          item.outputOptions.moduletype !== "",
      );

      const qwizbForThisControllerIO = qwizbItems.filter(
        (item) =>
          item.outputOptions.ctdId === thisControllerId &&
          item.outputOptions.moduletype === "",
      );

      const qwizbForThisControllerMod = qwizbItems.filter(
        (item) =>
          item.outputOptions.ctdId === thisControllerId &&
          item.outputOptions.moduletype !== "",
      );

      ctdKey.forEach((modKey, modValue) => {
        const dmxForThisControllerIO = dmxItems.filter(
          (item) =>
            item.outputOptions.ctdId === thisControllerId &&
            item.outputOptions.moduletype === "",
        );

        const dmxForThisControllerMod = dmxItems.filter(
          (item) =>
            item.outputOptions.ctdId === thisControllerId &&
            item.outputOptions.moduletype !== "",
        );

        const qdmForThisControllerIO = qdmItems.filter(
          (item) =>
            item.outputOptions.ctdId === thisControllerId &&
            item.outputOptions.moduletype === "",
        );

        const qdmForThisControllerMod = qdmItems.filter(
          (item) =>
            item.outputOptions.ctdId === thisControllerId &&
            item.outputOptions.moduletype !== "",
        );

        const normalizedModuleType = String(modValue).toUpperCase();
        const specialItemsForModule =
          normalizedModuleType === "DMX"
            ? ctdValue.startsWith("ctdIO")
              ? dmxForThisControllerIO
              : dmxForThisControllerMod
            : normalizedModuleType === "QDM" ||
                normalizedModuleType === "QDM01" ||
                normalizedModuleType === "DALIM"
              ? ctdValue.startsWith("ctdIO")
                ? qdmForThisControllerIO
                : qdmForThisControllerMod
              : undefined;

        const newModules = ctdValue.startsWith("ctdIO")
          ? buildRealModulesMap(
              modValue,
              modKey,
              onlySa,
              rel04_08,
              true,
              thisControllerId,
              specialItemsForModule,
            )
          : buildRealModulesMap(
              modValue,
              modKey,
              false,
              true,
              false,
              thisControllerId,
              specialItemsForModule,
            );
        //: buildRealModulesMapForModules(modValue, modKey, false, true);

        mergeModuleMaps(module_modulesMap, newModules);
      });

      const easeewaveCount = ctdValue.startsWith("ctdIO")
        ? easeewaveForThisControllerIO.length
        : easeewaveForThisControllerMod.length;

      if (easeewaveCount > 0) {
        const easeewaveModules = buildRealModulesMap(
          "QWI/EW",
          easeewaveCount,
          false,
          true,
          false,
          thisControllerId,
        );

        mergeModuleMaps(module_modulesMap, easeewaveModules);
      }

      const qwizbCount = ctdValue.startsWith("ctdIO")
        ? qwizbForThisControllerIO.length
        : qwizbForThisControllerMod.length;

      if (qwizbCount > 0) {
        const qwizbModules = buildRealModulesMap(
          "QWI/ZB",
          qwizbCount,
          false,
          true,
          false,
          thisControllerId,
        );

        mergeModuleMaps(module_modulesMap, qwizbModules);
      }

      mergeCtdMaps(module_ctds, ctdValue, module_modulesMap);
    });
  });

  return module_kastenMap;
}
